// Shipment facts on a PO (ship date / ETA / tracking / vendor order #) — manual edit uses the same writer as the
// vendor-email matcher (PATCH /purchasing/pos/{id}/shipment), so the WO note + history stay in step.
import { useState } from "react";
import { ExternalLink, Truck } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { btn } from "@/components/common/btn";
import { patchPOShipment } from "@/lib/api";
import { shortDate } from "@/lib/format";

export const CARRIERS = ["UPS", "FedEx", "USPS", "DHL", "LTL Freight", "Other"];
const TRACK_URL = {
  ups: "https://www.ups.com/track?tracknum={n}",
  fedex: "https://www.fedex.com/fedextrack/?trknbr={n}",
  usps: "https://tools.usps.com/go/TrackConfirmAction?tLabels={n}",
  dhl: "https://www.dhl.com/en/express/tracking.html?AWB={n}",
};
const cleanTracking = (raw) => (raw || "").replace(/[\s-]/g, "");
export const detectCarrier = (raw) => {
  const n = cleanTracking(raw);
  if (!n) return null;
  if (/^1Z[0-9A-Z]{16}$/i.test(n)) return "UPS";
  if (/^(\d{12}|\d{15}|\d{20}|\d{22})$/.test(n)) return "FedEx";
  if (/^(9[2-5]\d{20,24}|[A-Z]{2}\d{9}US)$/i.test(n)) return "USPS";
  if (/^\d{10}$/.test(n)) return "DHL";
  return null;
};
export const trackingUrl = (carrier, number) => {
  const n = cleanTracking(number);
  if (!n) return null;
  const tpl = TRACK_URL[(carrier || detectCarrier(n) || "").toLowerCase()];
  return tpl ? tpl.replace("{n}", n) : null;
};

export function TrackingLink({ po, testId, className = "", compact = false }) {
  const n = po.tracking_number;
  if (!n) return <span className="text-slate-400">—</span>;
  const url = po.tracking_url || trackingUrl(po.carrier, n);
  const carrier = po.carrier || detectCarrier(cleanTracking(n));
  // compact = carrier name only (the number lives in the tooltip) — the grid stays clean, one click tracks.
  const label = compact ? (carrier || "Track") : `${carrier ? `${carrier} ` : ""}${n}`;
  if (!url) return <span className={`font-mono ${className}`} title={compact ? n : undefined} data-testid={testId}>{label}</span>;
  return (
    <a href={url} target="_blank" rel="noreferrer" title={compact ? `Track ${n}` : "Open carrier tracking"} onClick={(e) => e.stopPropagation()}
      className={`inline-flex items-center gap-1 ${compact ? "font-medium" : "font-mono"} text-brand hover:underline ${className}`} data-testid={testId}>
      {label}<ExternalLink className="h-3 w-3" />
    </a>
  );
}

// One-line readout: "Ships 6/12/26 · ETA 6/15/26 · UPS 1Z…"; renders nothing when the vendor has told us nothing.
export function ShipmentSummary({ po, testId, className = "" }) {
  if (!po.vendor_ship_date && !po.eta && !po.tracking_number) return null;
  return (
    <div className={`flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-600 ${className}`} data-testid={testId}>
      <Truck className="h-3.5 w-3.5 text-slate-400" />
      {po.vendor_ship_date && <span>Ships <span className="num">{shortDate(po.vendor_ship_date)}</span></span>}
      {po.eta && <span>ETA <span className="num">{shortDate(po.eta)}</span></span>}
      {po.tracking_number && <TrackingLink po={po} />}
      {po.last_vendor_update_at && <span className="text-slate-400">vendor update {shortDate(po.last_vendor_update_at)}</span>}
    </div>
  );
}

const FIELDS = ["vendor_ship_date", "eta", "tracking_number", "carrier", "vendor_order_number"];
const FIELD_LABEL = { vendor_ship_date: "ship date", eta: "ETA", tracking_number: "tracking", carrier: "carrier", vendor_order_number: "vendor order #" };

export function ShipmentDialog({ po, onClose, onSaved }) {
  const [form, setForm] = useState({
    vendor_ship_date: po.vendor_ship_date || "", eta: po.eta || "", tracking_number: po.tracking_number || "",
    carrier: po.carrier || "", vendor_order_number: po.vendor_order_number || "", note: "",
  });
  const [saving, setSaving] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async () => {
    const clear = FIELDS.filter((f) => po[f] && !form[f].trim());
    const payload = { clear };
    FIELDS.forEach((f) => { if (form[f].trim()) payload[f] = form[f].trim(); });
    if (form.note.trim()) payload.note = form.note.trim();
    if (payload.tracking_number && !payload.carrier) payload.carrier = detectCarrier(payload.tracking_number) || undefined;
    setSaving(true);
    try {
      const { data } = await patchPOShipment(po.id, payload);
      toast.success(data.changed ? "Shipment updated — note added to the WO" : "No changes to save");
      onSaved?.(data.po);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not save shipment details"); }
    finally { setSaving(false); }
  };

  const history = (po.shipment_history || []).slice().reverse().slice(0, 6);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[92vh] w-[calc(100%-1rem)] max-w-lg overflow-y-auto" data-testid={`shipment-dialog-${po.id}`}>
        <DialogHeader>
          <DialogTitle className="text-base">Tracking / ETA — PO <span className="font-mono">{po.po_number}</span></DialogTitle>
          <DialogDescription className="text-xs">What the vendor told you. Saving writes a note on the work order; receiving stays separate.</DialogDescription>
        </DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="text-[10px] font-medium text-slate-500">Ship Date</label>
            <Input type="date" value={form.vendor_ship_date} onChange={set("vendor_ship_date")} className="h-9 text-sm" data-testid="shipment-ship-date" />
          </div>
          <div>
            <label className="text-[10px] font-medium text-slate-500">ETA</label>
            <Input type="date" value={form.eta} onChange={set("eta")} className="h-9 text-sm" data-testid="shipment-eta" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[10px] font-medium text-slate-500">Tracking #</label>
            <Input value={form.tracking_number} onChange={set("tracking_number")} placeholder="1Z…" className="h-9 font-mono text-sm" data-testid="shipment-tracking" />
          </div>
          <div className="col-span-2 sm:col-span-1">
            <label className="text-[10px] font-medium text-slate-500">Carrier</label>
            <select value={form.carrier} onChange={set("carrier")} className="h-9 w-full rounded-md border border-slate-200 bg-white px-2 text-sm" data-testid="shipment-carrier">
              <option value="">{form.tracking_number ? `Auto (${detectCarrier(form.tracking_number) || "unknown"})` : "—"}</option>
              {CARRIERS.map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-medium text-slate-500">Vendor Order / Confirmation #</label>
            <Input value={form.vendor_order_number} onChange={set("vendor_order_number")} className="h-9 text-sm" data-testid="shipment-vendor-order" />
          </div>
          <div className="col-span-2">
            <label className="text-[10px] font-medium text-slate-500">Note for the WO (optional)</label>
            <Input value={form.note} onChange={set("note")} placeholder="e.g., vendor called — backordered one week" className="h-9 text-sm" data-testid="shipment-note" />
          </div>
        </div>
        <div className="flex items-center justify-end gap-2">
          <button type="button" className={btn("nav")} onClick={onClose} data-testid="shipment-cancel">Cancel</button>
          <button type="button" className={btn("write")} onClick={save} disabled={saving} data-testid="shipment-save">{saving ? "Saving…" : "Save"}</button>
        </div>
        {history.length > 0 && (
          <div className="border-t pt-2">
            <div className="mb-1 text-xs font-semibold uppercase tracking-wide text-slate-500">Shipment History</div>
            {history.map((h) => (
              <div key={h.id} className="text-xs text-slate-500" data-testid={`shipment-history-${h.id}`}>
                {new Date(h.at).toLocaleString()} · {h.by} · {(h.changes || []).map((c) => `${FIELD_LABEL[c.field] || c.field} → ${c.field.endsWith("date") || c.field === "eta" ? shortDate(c.to) : (c.to || "cleared")}`).join(", ")}
                {h.message && ` · "${h.message}"`}
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
